/**
 * Costs of the expéditions (Functional Specifications 11.2, FR-SUI-10 and FR-SUI-11, BR-19):
 * contracts with the prestataires and the cost lines they give each expédition, in dirhams.
 * The computation is a pure function so that the API, the tests and later the analyses share it.
 */
import { z } from 'zod';
/**
 * The kinds of cost an expédition carries (FR-SUI-10); the rental of conditionnements and the
 * frais annexes are fixed costs of the site (FR-SUI-12).
 */
export const EXPEDITION_COST_KINDS = [
    'TRANSPORT',
    'TRAITEMENT',
    'TAXE',
    'ACHAT_CONDITIONNEMENT',
    'RECETTE',
    'AUTRE_OPERATION',
];
/**
 * How a contract prices an expédition. A monthly fee is a fixed cost of the site (FR-SUI-12),
 * not a line of an expédition, and comes with the fixed costs.
 */
export const PRICING_BASES = ['PER_TONNE', 'PER_CONTAINER', 'PER_ROTATION'];
export const COST_UNITS = ['T', 'CONTAINER', 'ROTATION', 'FIXED'];
export const UNIT_OF_BASIS = {
    PER_TONNE: 'T',
    PER_CONTAINER: 'CONTAINER',
    PER_ROTATION: 'ROTATION',
};
/** The expéditions that carry costs: from the pickup on, cancelled ones excepted. */
export const COSTED_STATUSES = [
    'SHIPPED',
    'TEMP_STORED',
    'RECEIVED',
    'ACCEPTED',
    'REFUSED',
    'FORWARDED',
    'TREATED',
];
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Date must be YYYY-MM-DD');
const uuid = z.string().uuid();
const amount = z.number().min(-1_000_000_000).max(1_000_000_000);
const coverage = {
    /** Coverage: empty means every waste, first destination or conditionnement. */
    wasteIds: z.array(uuid).max(1000),
    installationIds: z.array(uuid).max(500),
    conditionnementIds: z.array(uuid).max(500),
};
const contractBase = z.object({
    companyId: uuid,
    prestataireId: uuid,
    kind: z.enum(EXPEDITION_COST_KINDS),
    number: z.string().trim().max(60).optional(),
    label: z.string().trim().max(160).optional(),
    price: z.number().nonnegative().max(1_000_000_000),
    pricingBasis: z.enum(PRICING_BASES),
    minimumBilling: z.number().nonnegative().max(1_000_000_000).nullable().optional(),
    validFrom: isoDate,
    validTo: isoDate.nullable().optional(),
    ...coverage,
    notes: z.string().trim().max(1000).optional(),
});
const validityOrder = (c) => !c.validFrom || !c.validTo || c.validTo >= c.validFrom;
const validityMessage = { message: 'The end of validity is before its start', path: ['validTo'] };
export const contractCreateSchema = contractBase
    .extend({
    wasteIds: coverage.wasteIds.default([]),
    installationIds: coverage.installationIds.default([]),
    conditionnementIds: coverage.conditionnementIds.default([]),
})
    .refine(validityOrder, validityMessage);
/** Only the fields sent change; the company and the prestataire of a contract never do. */
export const contractUpdateSchema = contractBase
    .omit({ companyId: true, prestataireId: true })
    .partial()
    .extend({ active: z.boolean().optional() })
    .refine(validityOrder, validityMessage);
export const contractListQuerySchema = z.object({
    companyId: uuid.optional(),
    prestataireId: uuid.optional(),
    includeInactive: z
        .enum(['true', 'false'])
        .optional()
        .transform((v) => v === 'true'),
});
/** A line typed by finance, or a computed line corrected, always with its reason (FR-SUI-10). */
export const manualCostLineSchema = z.object({
    kind: z.enum(EXPEDITION_COST_KINDS),
    prestataireId: uuid.nullable().optional(),
    amount,
    quantity: z.number().nonnegative().max(1_000_000).nullable().optional(),
    unit: z.enum(COST_UNITS).nullable().optional(),
    reason: z.string().trim().min(3).max(500),
});
/** FR-SUI-12: the fixed costs of a site per month. */
export const FIXED_COST_KINDS = [
    'LOCATION_CONDITIONNEMENT',
    'FRAIS_ANNEXES',
    'ABONNEMENT',
    'AUTRE',
];
export const fixedCostCreateSchema = z.object({
    siteId: uuid,
    /** The month the cost covers, "YYYY-MM". */
    month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'Month must be YYYY-MM'),
    kind: z.enum(FIXED_COST_KINDS),
    label: z.string().trim().min(1).max(160),
    prestataireId: uuid.nullable().optional(),
    amount: z.number().min(0).max(1_000_000_000),
    comment: z.string().trim().max(1000).nullable().optional(),
});
export const fixedCostUpdateSchema = fixedCostCreateSchema.omit({ siteId: true }).partial();
export const costOverrideSchema = z.object({
    amount,
    reason: z.string().trim().min(3).max(500),
});
const round2 = (n) => Math.round(n * 100) / 100;
const round4 = (n) => Math.round(n * 10_000) / 10_000;
/** The prestataires of the expédition a contract of this kind may bill. */
export function billablePrestataires(e, kind) {
    const transporter = e.transporterId ? [e.transporterId] : [];
    const destination = e.firstDestination ? [e.firstDestination.prestataireId] : [];
    const trader = e.traderId ? [e.traderId] : [];
    switch (kind) {
        case 'TRANSPORT':
            return transporter;
        case 'TRAITEMENT':
            return destination;
        case 'RECETTE':
            return [...new Set([...trader, ...destination])];
        default:
            return [...new Set([...transporter, ...trader, ...destination])];
    }
}
/**
 * How specifically a contract covers the expédition (BR-19: waste, destination and conditionnement
 * over waste alone over all); null when its coverage excludes it.
 */
export function coverageSpecificity(c, e) {
    let score = 0;
    if (c.wasteIds.length) {
        if (!c.wasteIds.includes(e.wasteId))
            return null;
        score += 4;
    }
    if (c.installationIds.length) {
        if (!e.firstDestination || !c.installationIds.includes(e.firstDestination.installationId)) {
            return null;
        }
        score += 2;
    }
    if (c.conditionnementIds.length) {
        if (!e.packages.some((p) => c.conditionnementIds.includes(p.conditionnementId)))
            return null;
        score += 1;
    }
    return score;
}
const validOn = (c, day) => c.active && c.validFrom <= day && (!c.validTo || day <= c.validTo);
/** The quantity a kind of cost is billed on: treatment and revenue only on what was accepted. */
function tonnes(e, kind) {
    const received = e.quantityReceivedT;
    const base = received ?? e.quantityT;
    const source = received === null ? 'PRODUCER' : 'RECEIVED';
    if (kind === 'TRAITEMENT' || kind === 'RECETTE') {
        if (e.status === 'REFUSED')
            return { quantity: 0, source };
        return { quantity: Math.max(0, base - (e.quantityRefusedT ?? 0)), source };
    }
    return { quantity: base, source };
}
/** The contract each billable prestataire and kind applies to an expédition, if any. */
export function applicableContracts(e, contracts) {
    const best = new Map();
    for (const c of contracts) {
        if (!validOn(c, e.date))
            continue;
        if (!billablePrestataires(e, c.kind).includes(c.prestataireId))
            continue;
        const specificity = coverageSpecificity(c, e);
        if (specificity === null)
            continue;
        const key = `${c.kind}:${c.prestataireId}`;
        const current = best.get(key);
        if (!current ||
            specificity > current.specificity ||
            (specificity === current.specificity &&
                (c.validFrom > current.contract.validFrom ||
                    (c.validFrom === current.contract.validFrom && c.id > current.contract.id)))) {
            best.set(key, { contract: c, specificity });
        }
    }
    return [...best.values()];
}
/**
 * BR-19: the cost lines of the expéditions of one chargement, from the contracts valid on their
 * date whose coverage matches most specifically, on the received quantity or, failing that, the
 * producer quantity, with the contract's minimum billing. A rotation is shared between the
 * expéditions of the chargement it covers, in proportion to their tonnage. A revenue is negative.
 * `manual` lists the kind and prestataire pairs finance entered by hand, which the computation
 * leaves alone.
 */
export function computeCostLines(expeditions, contracts, manual = new Map()) {
    const lines = [];
    const rotations = new Map();
    for (const e of expeditions) {
        if (!COSTED_STATUSES.includes(e.status))
            continue;
        const handTyped = manual.get(e.id) ?? new Set();
        for (const { contract: c, specificity } of applicableContracts(e, contracts)) {
            if (handTyped.has(`${c.kind}:${c.prestataireId}`))
                continue;
            const sign = c.kind === 'RECETTE' ? -1 : 1;
            if (c.pricingBasis === 'PER_ROTATION') {
                const t = tonnes(e, c.kind);
                if ((c.kind === 'TRAITEMENT' || c.kind === 'RECETTE') && t.quantity === 0)
                    continue;
                const group = rotations.get(c.id) ?? { contract: c, specificity, members: [] };
                group.members.push({ e, t: t.quantity });
                rotations.set(c.id, group);
                continue;
            }
            let quantity;
            let quantitySource;
            if (c.pricingBasis === 'PER_TONNE') {
                const t = tonnes(e, c.kind);
                quantity = t.quantity;
                quantitySource = t.source;
            }
            else {
                quantity = e.packages
                    .filter((p) => !c.conditionnementIds.length || c.conditionnementIds.includes(p.conditionnementId))
                    .reduce((sum, p) => sum + p.count, 0);
                quantitySource = 'CONTAINERS';
            }
            if (quantity <= 0)
                continue;
            const raw = quantity * c.price;
            const minimum = c.kind === 'RECETTE' ? null : c.minimumBilling;
            const minimumApplied = minimum !== null && raw < minimum;
            lines.push({
                expeditionId: e.id,
                kind: c.kind,
                prestataireId: c.prestataireId,
                contractId: c.id,
                pricingBasis: c.pricingBasis,
                quantity: round4(quantity),
                unit: UNIT_OF_BASIS[c.pricingBasis],
                unitPrice: c.price,
                amount: round2(sign * (minimumApplied ? minimum : raw)),
                basis: { contractNumber: c.number, quantitySource, minimumApplied, specificity },
            });
        }
    }
    // a rotation is billed once for the chargement, shared by tonnage
    for (const { contract: c, specificity, members } of rotations.values()) {
        const minimum = c.kind === 'RECETTE' ? null : c.minimumBilling;
        const total = minimum !== null && c.price < minimum ? minimum : c.price;
        const sign = c.kind === 'RECETTE' ? -1 : 1;
        const tonnage = members.reduce((sum, m) => sum + m.t, 0);
        let allocated = 0;
        members.forEach((m, index) => {
            const share = tonnage > 0 ? m.t / tonnage : 1 / members.length;
            // the last member takes the rounding remainder so that the shares add up to the rotation
            const value = index === members.length - 1 ? round2(total - allocated) : round2(total * share);
            allocated = round2(allocated + value);
            lines.push({
                expeditionId: m.e.id,
                kind: c.kind,
                prestataireId: c.prestataireId,
                contractId: c.id,
                pricingBasis: 'PER_ROTATION',
                quantity: round4(share),
                unit: 'ROTATION',
                unitPrice: c.price,
                amount: round2(sign * value),
                basis: {
                    contractNumber: c.number,
                    quantitySource: 'ROTATION',
                    minimumApplied: minimum !== null && c.price < minimum,
                    specificity,
                    rotationShare: round4(share),
                },
            });
        });
    }
    return lines;
}
//# sourceMappingURL=costs.js.map